import { create } from 'zustand';
import { getEmployees } from '../api/generated/employees/employees';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PAGE_SIZE } from '@/constants/pagination';
import type {
  FacilityFlowCoreModulesEmployeesDtosEmployeeDto,
  FacilityFlowCoreModulesEmployeesDtosEmployeeListDto,
  FacilityFlowCoreModulesEmployeesDtosCreateEmployeeRequest,
  FacilityFlowCoreModulesEmployeesDtosUpdateEmployeeRequest,
  FacilityFlowCoreModulesEmployeesDtosUpdateOwnProfileRequest,
  FacilityFlowCoreModulesEmployeesDtosChangePasswordRequest,
} from '../api/generated/models';

interface EmployeeState {
  employees: FacilityFlowCoreModulesEmployeesDtosEmployeeListDto[];
  currentEmployee: FacilityFlowCoreModulesEmployeesDtosEmployeeDto | null;
  totalCount: number;
  loading: boolean;
  error: string | null;
}

interface EmployeeStore extends EmployeeState {
  fetchEmployees: (params?: {
    search?: string;
    status?: string;
    role?: string;
    page?: number;
    pageSize?: number;
  }) => Promise<void>;
  fetchAllEmployees: (filter?: {
    search?: string;
    status?: string;
    role?: string;
  }) => Promise<void>;
  fetchEmployeeById: (id: string) => Promise<void>;
  createEmployee: (
    data: FacilityFlowCoreModulesEmployeesDtosCreateEmployeeRequest
  ) => Promise<void>;
  updateEmployee: (
    id: string,
    data: FacilityFlowCoreModulesEmployeesDtosUpdateEmployeeRequest
  ) => Promise<void>;
  /** Assign (or clear via null) the work schedule model without touching other profile data. */
  assignWorkScheduleModel: (id: string, workScheduleModelId: string | null) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  updateOwnProfile: (
    data: FacilityFlowCoreModulesEmployeesDtosUpdateOwnProfileRequest
  ) => Promise<void>;
  changeOwnPassword: (
    data: FacilityFlowCoreModulesEmployeesDtosChangePasswordRequest
  ) => Promise<void>;
  resetPassword: (id: string, newPassword: string) => Promise<void>;
  activateEmployee: (id: string) => Promise<void>;
  deactivateEmployee: (id: string) => Promise<void>;
  terminateEmployee: (id: string, terminationDate: string) => Promise<void>;
  clearError: () => void;
}

const {
  getApiV1Employees,
  getApiV1EmployeesId,
  postApiV1Employees,
  putApiV1EmployeesId,
  putApiV1EmployeesIdWorkScheduleModel,
  deleteApiV1EmployeesId,
  putApiV1EmployeesMeProfile,
  postApiV1EmployeesMeChangePassword,
  postApiV1EmployeesIdResetPassword,
  postApiV1EmployeesIdActivate,
  postApiV1EmployeesIdDeactivate,
  postApiV1EmployeesIdTerminate,
} = getEmployees();

export const useEmployeeStore = create<EmployeeStore>((set) => ({
  employees: [],
  currentEmployee: null,
  totalCount: 0,
  loading: false,
  error: null,

  fetchEmployees: async (params) => {
    set({ loading: true, error: null });
    try {
      const response = await getApiV1Employees(params);
      set({
        employees: response.employees || [],
        totalCount: response.totalCount || 0,
        loading: false,
      });
    } catch {
      set({
        error: 'Fehler beim Laden der Mitarbeiter',
        loading: false,
      });
    }
  },

  fetchAllEmployees: async (filter) => {
    set({ loading: true, error: null });
    try {
      const all: FacilityFlowCoreModulesEmployeesDtosEmployeeListDto[] = [];
      let total = 0;
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const response = await getApiV1Employees({
          ...filter,
          page,
          pageSize: FETCH_ALL_PAGE_SIZE,
        });
        const items = response.employees ?? [];
        all.push(...items);
        total = response.totalCount ?? all.length;
        if (items.length === 0 || all.length >= total) break;
      }
      set({ employees: all, totalCount: total, loading: false });
    } catch {
      set({
        error: 'Fehler beim Laden der Mitarbeiter',
        loading: false,
      });
    }
  },

  fetchEmployeeById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const employee = await getApiV1EmployeesId(id);
      set({
        currentEmployee: employee,
        loading: false,
      });
    } catch {
      set({
        error: 'Fehler beim Laden des Mitarbeiters',
        loading: false,
      });
    }
  },

  createEmployee: async (data) => {
    set({ loading: true, error: null });
    try {
      await postApiV1Employees(data);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Erstellen des Mitarbeiters',
        loading: false,
      });
      throw err;
    }
  },

  updateEmployee: async (id: string, data) => {
    set({ loading: true, error: null });
    try {
      await putApiV1EmployeesId(id, data);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Aktualisieren des Mitarbeiters',
        loading: false,
      });
      throw err;
    }
  },

  assignWorkScheduleModel: async (id: string, workScheduleModelId: string | null) => {
    set({ loading: true, error: null });
    try {
      await putApiV1EmployeesIdWorkScheduleModel(id, { workScheduleModelId });
      set((state) => ({
        loading: false,
        employees: state.employees.map((e) =>
          e.id === id ? { ...e, workScheduleModelId } : e
        ),
        currentEmployee:
          state.currentEmployee?.id === id
            ? { ...state.currentEmployee, workScheduleModelId }
            : state.currentEmployee,
      }));
    } catch (err) {
      set({
        error: 'Fehler beim Zuweisen des Arbeitszeitmodells',
        loading: false,
      });
      throw err;
    }
  },

  deleteEmployee: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await deleteApiV1EmployeesId(id);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Löschen des Mitarbeiters',
        loading: false,
      });
      throw err;
    }
  },

  updateOwnProfile: async (data) => {
    set({ loading: true, error: null });
    try {
      await putApiV1EmployeesMeProfile(data);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Aktualisieren des Profils',
        loading: false,
      });
      throw err;
    }
  },

  changeOwnPassword: async (data) => {
    set({ loading: true, error: null });
    try {
      await postApiV1EmployeesMeChangePassword(data);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Ändern des Passworts',
        loading: false,
      });
      throw err;
    }
  },

  resetPassword: async (id: string, newPassword: string) => {
    set({ loading: true, error: null });
    try {
      await postApiV1EmployeesIdResetPassword(id, { newPassword });
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Zurücksetzen des Passworts',
        loading: false,
      });
      throw err;
    }
  },

  activateEmployee: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await postApiV1EmployeesIdActivate(id);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Aktivieren des Mitarbeiters',
        loading: false,
      });
      throw err;
    }
  },

  deactivateEmployee: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await postApiV1EmployeesIdDeactivate(id);
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Deaktivieren des Mitarbeiters',
        loading: false,
      });
      throw err;
    }
  },

  terminateEmployee: async (id: string, terminationDate: string) => {
    set({ loading: true, error: null });
    try {
      await postApiV1EmployeesIdTerminate(id, { terminationDate });
      set({ loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Beenden des Arbeitsverhältnisses',
        loading: false,
      });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
